/**
 * Utility functions for Demo Mode
 * These functions mask sensitive data for demonstration purposes
 * All masking is visual only - no data is modified
 */

/**
 * Masks email when demo mode is active
 */
export const maskEmail = (email: string | null | undefined, isDemoMode: boolean): string => {
  if (isDemoMode) return 'usuario@email.com';
  return email || '';
};

/**
 * Masks generator name when demo mode is active
 */
export const maskGeneratorName = (name: string | null | undefined, isDemoMode: boolean): string => {
  if (isDemoMode) return 'GERADOR';
  return name || 'N/A';
};

/**
 * Masks hauler (transportadora) name when demo mode is active
 */
export const maskHaulerName = (name: string | null | undefined, isDemoMode: boolean): string => {
  if (isDemoMode) return 'TRANSPORTADORA';
  return name || 'N/A';
};

/**
 * Masks establishment name when demo mode is active
 */
export const maskEstablishmentName = (name: string | null | undefined, isDemoMode: boolean): string => {
  if (isDemoMode) return 'ESTABELECIMENTO';
  return name || 'N/A';
};

/**
 * Masks CNPJ when demo mode is active
 */
export const maskCNPJ = (cnpj: string | null | undefined, isDemoMode: boolean): string => {
  if (isDemoMode) return '12.345.678/0001-23';
  return cnpj || 'N/A';
};

/**
 * Masks phone number when demo mode is active
 */
export const maskPhoneNumber = (phone: string | null | undefined, isDemoMode: boolean): string => {
  if (isDemoMode) return '(11) 99999-9999';
  return phone || '';
};

/**
 * Masks address when demo mode is active
 */
export const maskAddress = (address: string | null | undefined, isDemoMode: boolean): string => {
  if (isDemoMode) return 'ENDEREÇO OCULTADO';
  return address || '';
};

/** Substitui os valores numéricos de materialTotals por fictícios em modo demo,
 *  preservando o fator de conversão CO2/kg real de cada material. */
export const maskMaterialTotals = <T extends { weight_collected: number; carbon_saved: number }>(
  items: T[],
  isDemoMode: boolean,
): T[] => {
  if (!isDemoMode) return items;
  return items.map((m, i) => {
    const fakeWeight = (items.length - i) * 1000;
    const factor = m.weight_collected > 0 ? m.carbon_saved / m.weight_collected : 0;
    return {
      ...m,
      weight_collected: fakeWeight,
      carbon_saved: Math.round(fakeWeight * factor),
    };
  });
};

const DEMO_RANKING_VALUES = [5000, 4000, 3000, 2000, 1000];
const DEMO_GROUP_VALUES = [300, 200, 100];
const DEMO_MATERIAL_VALUES = [150, 100, 50];

/** Substitui value, co2 e breakdown dos itens de ranking por fictícios fixos em modo demo. */
export const maskRankingData = <T extends {
  value: number;
  co2: number;
  breakdown: { co2: number; materials: { co2: number }[] }[];
}>(items: T[], isDemoMode: boolean): T[] => {
  if (!isDemoMode) return items;
  return items.map((r, i) => ({
    ...r,
    value: DEMO_RANKING_VALUES[i] ?? 500,
    co2: DEMO_RANKING_VALUES[i] ?? 500,
    breakdown: r.breakdown.map((g, j) => ({
      ...g,
      co2: DEMO_GROUP_VALUES[j] ?? 50,
      materials: g.materials.map((m, k) => ({
        ...m,
        co2: DEMO_MATERIAL_VALUES[k] ?? 25,
      })),
    })),
  }));
};

/**
 * Masks user name when demo mode is active, using index for uniqueness
 */
export const maskUserName = (name: string | null | undefined, isDemoMode: boolean, index?: number): string => {
  if (isDemoMode) return index !== undefined ? `Usuário ${index + 1}` : 'Usuário';
  return name || 'Sem nome';
};

/** Substitui um valor numérico por um fictício fixo (1234) em modo demo. */
export const maskNumericValue = (value: number, isDemoMode: boolean): number => {
  if (!isDemoMode) return value;
  return 1234;
};

/** Substitui uma porcentagem (0–100) por um valor fictício (87,5) em modo demo. */
export const maskPercentage = (value: number, isDemoMode: boolean): number => {
  if (!isDemoMode) return value;
  return 87.5;
};

/** Valores cíclicos para preencher séries temporais (por dia/mês) em modo demo,
 *  evitando uma tendência crescente/decrescente artificial que os dados reais não têm. */
const DEMO_TIME_SERIES_VALUES = [80, 120, 60, 150, 100, 90, 130, 70, 110, 95, 140, 85];

const demoTimeSeriesValue = (index: number, scale = 1): number =>
  Math.round(DEMO_TIME_SERIES_VALUES[index % DEMO_TIME_SERIES_VALUES.length] * scale);

/** Fictício decrescente por posição — usado em listas já ranqueadas (do maior pro menor). */
const demoRankedValue = (index: number, base: number): number =>
  Math.max(20, Math.round(base / (index + 1)));

/** Mascara os totais agregados dos KPI cards do Overview. */
export const maskDashboardTotals = <T extends {
  total_collects: number;
  total_units: number;
  total_volume: number;
  total_weight: number;
  total_establishments_with_collects: number;
  success_rate: number;
  mtr_rate: number | null;
  variation_percent: number | null;
}>(totals: T, isDemoMode: boolean): T => {
  if (!isDemoMode) return totals;
  return {
    ...totals,
    total_collects: 1234,
    total_units: 2345,
    total_volume: 3456,
    total_weight: 4567,
    total_establishments_with_collects: 12,
    success_rate: 87.5,
    mtr_rate: 92.3,
    variation_percent: totals.variation_percent === null ? null : 5.2,
  };
};

/** Mascara a série "Coletas ao longo do tempo" (por material/dia). */
export const maskByMaterialPerDay = <T extends {
  total_collects: number;
  total_units: number;
  total_volume: number;
  total_weight: number;
}>(items: T[], isDemoMode: boolean): T[] => {
  if (!isDemoMode) return items;
  return items.map((item, i) => {
    const base = demoTimeSeriesValue(i);
    return {
      ...item,
      total_collects: Math.round(base / 10),
      total_units: base,
      total_volume: base * 5,
      total_weight: base * 8,
    };
  });
};

/** Mascara nome e volume das unidades no heatmap "Volume coletado", preservando a
 *  proporção diária de cada unidade e a ordenação por volume total. */
export const maskVolumeByEstablishment = <T extends {
  establishment_id: number;
  establishment_name: string;
  total_volume: number;
  establishment_total_volume: number;
}>(rows: T[], isDemoMode: boolean): T[] => {
  if (!isDemoMode) return rows;
  const orderedIds = [...new Set(
    [...rows]
      .sort((a, b) => b.establishment_total_volume - a.establishment_total_volume)
      .map((r) => r.establishment_id),
  )];
  return rows.map((row) => {
    const rank = orderedIds.indexOf(row.establishment_id);
    const fakeTotal = demoRankedValue(rank, 5000);
    const ratio = row.establishment_total_volume > 0
      ? row.total_volume / row.establishment_total_volume
      : 0;
    return {
      ...row,
      establishment_name: `Unidade ${rank + 1}`,
      establishment_total_volume: fakeTotal,
      total_volume: Math.round(fakeTotal * ratio),
    };
  });
};

/** Mascara contagem e percentual dos motivos de falha do widget "Taxa de sucesso". */
export const maskFaultReasons = <T extends { count: number; percentage: number }>(
  entries: T[],
  isDemoMode: boolean,
): T[] => {
  if (!isDemoMode) return entries;
  return entries.map((entry, i) => ({
    ...entry,
    count: demoRankedValue(i, 60),
    percentage: demoRankedValue(i, 40),
  }));
};

/** Mascara o carbono evitado por material do widget "Carbono evitado por resíduo". */
export const maskCarbonByMaterial = <T extends { carbon_saved: number }>(
  entries: T[],
  isDemoMode: boolean,
): T[] => {
  if (!isDemoMode) return entries;
  return entries.map((entry, i) => ({ ...entry, carbon_saved: demoRankedValue(i, 900) }));
};

/** Mascara as contagens do widget "Coletas recorrentes x sob demanda". */
export const maskCollectsByOrigin = <T extends {
  recurring_count: number;
  on_demand_count: number;
}>(entries: T[], isDemoMode: boolean): T[] => {
  if (!isDemoMode) return entries;
  return entries.map((entry, i) => ({
    ...entry,
    recurring_count: demoTimeSeriesValue(i, 1),
    on_demand_count: demoTimeSeriesValue(i + 3, 0.4),
  }));
};
